import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LoadingOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { servicesService } from '../../services/services.service';

const GitHubCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Conectando con GitHub...');
  const [debugInfo, setDebugInfo] = useState<any>(null);
  const hasProcessed = useRef(false);
  const isProcessing = useRef(false);

  useEffect(() => {
    if (hasProcessed.current || isProcessing.current) {
      return;
    }

    const handleCallback = async () => {
      isProcessing.current = true;

      try {
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const error = searchParams.get('error');

        setDebugInfo({
          code: code?.substring(0, 10) + '...',
          hasToken: !!localStorage.getItem('github_token'),
          timestamp: new Date().toISOString(),
        });

        if (error) {
          setStatus('error');
          setMessage('Acceso denegado. No se pudo conectar con GitHub.');
          hasProcessed.current = true;
          return;
        }

        if (!code) {
          setStatus('error');
          setMessage('No se recibió el código de autorización.');
          hasProcessed.current = true;
          return;
        }

        let serviceId: string | null = null;
        let appId: string | null = null;
        let tenantId: string | null = null;
        let returnUrl = '/deploy-apps';

        if (state) {
          try {
            const stateData = JSON.parse(atob(state));
            serviceId = stateData.serviceId;
            appId = stateData.appId;
            tenantId = stateData.tenantId;
            returnUrl = stateData.returnUrl || returnUrl;
          } catch (err) {
            console.error('[GitHubCallback] Error parsing state:', err);
          }
        }

        setMessage('Obteniendo token de acceso...');

        let response;
        try {
          const timeoutPromise = new Promise<never>((_, reject) => {
            setTimeout(() => reject(new Error('Timeout: La petición tardó demasiado')), 30000);
          });

          response = await Promise.race([
            servicesService.exchangeGithubCode(code),
            timeoutPromise
          ]);
        } catch (e: any) {
          console.error('[GitHubCallback] exchangeGithubCode failed:', e);
          throw e;
        }

        if (appId) {
          try {
            if (tenantId) {
              await servicesService.saveGithubToken(appId, response.access_token, tenantId);
            }
          } catch (e) {
            console.error('[GitHubCallback] Error saving token to backend:', e);
            throw new Error('Error al guardar el token de GitHub de forma segura.');
          }
        }

        setStatus('success');
        setMessage('¡Conectado exitosamente con GitHub!');

        setDebugInfo({
          success: true,
          tokenSaved: !!localStorage.getItem('github_token'),
          serviceId,
          appId,
          returnUrl,
          timestamp: new Date().toISOString(),
        });

        hasProcessed.current = true;

        // Redirigir con github_connected=true y app_id
        setTimeout(() => {
          if (serviceId) {
            const params = new URLSearchParams({ github_connected: 'true' });
            if (appId) {
              params.set('app_id', appId);
            }
            window.location.href = `/deploy-apps/${serviceId}?${params.toString()}`;
          } else {
            window.location.href = '/deploy-apps';
          }
        }, 2000);

      } catch (err: any) {
        console.error('[GitHubCallback] Error:', err);
        setStatus('error');

        setDebugInfo({
          error: {
            message: err.message,
            status: err.status,
            response: err.response,
          },
          timestamp: new Date().toISOString(),
        });

        if (err.message?.includes('Timeout')) {
          setMessage('La conexión con GitHub tardó demasiado. Por favor, intenta nuevamente.');
        } else if (err.message?.includes('incorrect or expired')) {
          setMessage('El código de autorización expiró. Por favor, intenta conectar GitHub nuevamente.');
        } else if (err.message?.includes('Unauthorized')) {
          setMessage('Error de autenticación. Tu sesión puede haber expirado.');
        } else if (err.message?.includes('Network')) {
          setMessage('Error de red. Verifica tu conexión e intenta nuevamente.');
        } else {
          setMessage(err.message || 'Error al conectar con GitHub. Por favor, intenta de nuevo.');
        }

        hasProcessed.current = true;
      } finally {
        isProcessing.current = false;
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  const handleContinue = () => {
    if (debugInfo?.serviceId) {
      const params = new URLSearchParams({ github_connected: 'true' });
      if (debugInfo.appId) {
        params.set('app_id', debugInfo.appId);
      }
      window.location.href = `/deploy-apps/${debugInfo.serviceId}?${params.toString()}`;
    } else {
      window.location.href = '/deploy-apps';
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
      <div className="max-w-2xl w-full bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8">
        <div className="text-center">
          <div className="mb-6">
            {status === 'loading' && (
              <LoadingOutlined className="text-6xl text-blue-600 dark:text-blue-400" spin />
            )}
            {status === 'success' && (
              <CheckCircleOutlined className="text-6xl text-green-600 dark:text-green-400" />
            )}
            {status === 'error' && (
              <CloseCircleOutlined className="text-6xl text-red-600 dark:text-red-400" />
            )}
          </div>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            {status === 'loading' && 'Conectando...'}
            {status === 'success' && '¡Éxito!'}
            {status === 'error' && 'Error'}
          </h2>

          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {message}
          </p>

          {status === 'loading' && (
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div className="bg-blue-600 h-2 rounded-full animate-pulse" style={{ width: '60%' }}></div>
            </div>
          )}

          <div className="mt-6 flex gap-3 justify-center">
            {status === 'success' && (
              <button
                onClick={handleContinue}
                className="px-6 py-3 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md transition-colors"
              >
                Continuar →
              </button>
            )}

            {status === 'error' && (
              <button
                onClick={() => navigate('/deploy-apps')}
                className="px-6 py-3 text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 rounded-md transition-colors"
              >
                Volver a Deploy Apps
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GitHubCallback;